import React, { useEffect, useMemo, useState } from 'react';
import { Form, Select } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { textFrom } from 'src/utils/textFrom';
import { SearchInput } from './inputs/SearchInput';

function hasPrevPage (currentPage, firstPage) {
  return currentPage > firstPage;
}

function hasNextPage (pagination) {
  return (pagination.startIndex || 0) + (pagination.itemsPerPage || 0) < (pagination.totalItems || 0);
}

export const SelectWithPagination = ({
  firstPage = 1,
  formItemName,
  items,
  getItemsAtPage,
  pagination,
  toOption,
  defaultOptions = [],
  label,
  rules,
  searchable = true,
  ...props
}) => {
  const { t } = useTranslation();
  const text = textFrom('components.selectWithPagination', t);

  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(firstPage);
  const [searchedText, setSearchedText] = useState();

  const OPTIONS = useMemo(() => {
    const itemOptions = items.map(toOption);
    const itemValues = new Set(itemOptions.map(({ value }) => value));
    return defaultOptions
      .filter(({ value }) => !itemValues.has(value))
      .concat(itemOptions);
  }, [items]);

  useEffect(async () => {
    setIsLoading(true);
    await getItemsAtPage(currentPage, searchedText);
    setIsLoading(false);
  }, [currentPage, searchedText]);

  const goToPrevPage = () => {
    if (!isLoading && hasPrevPage(currentPage, firstPage)) {
      setCurrentPage(p => p - 1);
    }
  };

  const goToNextPage = () => {
    if (!isLoading && hasNextPage(pagination)) {
      setCurrentPage(p => p + 1);
    }
  };

  const searchText = (txt) => {
    setSearchedText(txt);
    setCurrentPage(firstPage);
  };

  const handleClear = () => {
    if (props.onClear) {
      props.onClear();
    }
    setSearchedText(undefined);
    setCurrentPage(firstPage);
  };

  const paginationLabel = pagination.totalItems
    ? text('paginationCount', {
      from: pagination.startIndex + 1,
      to: pagination.startIndex + (pagination.currentItemCount ?? 0),
      total: pagination.totalItems
    })
    : '';

  const dropdownRender = (menu) => (
    <div>
      {searchable &&
        <div className='px-2 pt-2'>
          <SearchInput
            placeholder={text('searchPlaceholder')}
            onChangeText={searchText}
          />
        </div>}
      {menu}
      <div className='flex items-center justify-between px-3 py-2 border-t'>
        <LeftOutlined
          title={text('prevPage')}
          onClick={goToPrevPage}
          className={hasPrevPage(currentPage, firstPage) ? 'cursor-pointer' : 'opacity-30 cursor-not-allowed'}
        />
        <span>{paginationLabel}</span>
        <RightOutlined
          title={text('nextPage')}
          onClick={goToNextPage}
          className={hasNextPage(pagination) ? 'cursor-pointer' : 'opacity-30 cursor-not-allowed'}
        />
      </div>
    </div>
  );

  const select = (
    <Select
      allowClear
      virtual={false}
      optionFilterProp='label'
      filterOption={() => (true)}
      className='w-full'
      options={OPTIONS}
      dropdownRender={dropdownRender}
      onClear={handleClear}
      {...props}
      showSearch={false}
    />
  );

  if (!formItemName) {
    return select;
  }

  return (
    <Form.Item
      label={label}
      rules={rules}
      name={formItemName}
    >
      {select}
    </Form.Item>
  );
};
